import { Fragment, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";
import { Box, Grid } from "@mui/material";
import { useParams } from "react-router";

import { formatItem } from "@framework/exchange";
import type { IProduct } from "@framework/types";
import { Breadcrumbs, PageHeader, Spinner } from "@dzambalaorg/mui-page-layout";
import { ApiError, useApi } from "@dzambalaorg/provider-api-firebase";
import { RichTextDisplay } from "@dzambalaorg/mui-rte";
import { Typography } from "@dzambalaorg/ui-kit";

import { Carousel } from "./carousel";
import { StyledPaper } from "./styled";

export const Product = () => {
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();

  const [isLoading, setIsLoading] = useState(true);
  const [product, setProduct] = useState<IProduct>({} as IProduct);

  const api = useApi();

  const fetchProduct = async (): Promise<void> => {
    return api
      .fetchJson({
        url: `/products/${id!}`,
      })
      .then((json: IProduct) => {
        setProduct(json);
      })
      .catch((e: ApiError) => {
        if (e.status === 400) {
          const errors = e.getLocalizedValidationErrors();

          Object.keys(errors).forEach(key => {
            enqueueSnackbar(formatMessage({ id: errors[key] }, { label: key }), { variant: "error" });
          });
        } else if (e.status) {
          enqueueSnackbar(formatMessage({ id: `snackbar.${e.message}` }), { variant: "error" });
        } else {
          console.error(e);
          enqueueSnackbar(formatMessage({ id: "snackbar.error" }), { variant: "error" });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    void fetchProduct();
  }, []);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Fragment>
      <Breadcrumbs path={["dashboard", "ecommerce", "products"]} />

      <PageHeader message="pages.product.title" data={product} />

      <Grid container>
        <Grid size={{ xs: 12, md: 9 }}>
          <Carousel product={product} />
          <Box
            sx={theme => ({
              [theme.breakpoints.up("md")]: {
                display: "none",
              },
            })}
          >
            <Box py={2}>
              <StyledPaper>
                <Typography variant="body2" color="textSecondary" component="p">
                  {formatItem(product.productItems[0].price)}
                </Typography>
              </StyledPaper>
            </Box>
          </Box>
          <Typography variant="body2" color="textSecondary" component="div">
            <RichTextDisplay data={product.description} />
          </Typography>
        </Grid>
        <Box
          sx={theme => ({
            [theme.breakpoints.down("md")]: {
              display: "none",
            },
          })}
        >
          <Grid size={{ xs: 12, md: 3 }}>
            <StyledPaper>
              <Typography variant="body2" color="textSecondary" component="p">
                {formatItem(product.productItems[0].price)}
              </Typography>
            </StyledPaper>
          </Grid>
        </Box>
      </Grid>
    </Fragment>
  );
};
